import React, { FC, useEffect, useRef } from 'react';

import styled from 'styled-components';

import { useAdyenScript } from 'hooks/use-adyen-script';
import { useLocale } from 'state/intl';

import { AdyenAcceptedCards, AdyenLocales, IAdyenCustomCardInputs } from './types';

const AdyenInputsWrapper = styled.div`
  margin-bottom: 1.25rem;
  width: 100%;
  & label {
    > span {
      color: ${Styles.color.grey.one};
    }
    > div > [data-cse^='encrypted'] {
      border: 1px solid ${Styles.color.grey.four};
      min-height: 50px;
      border-radius: ${Styles.borderRadius};
    }
  }
`;

const AdyenCustomCardInputs: FC<IAdyenCustomCardInputs> = ({
  onChange,
  onError,
  onAdyenInputsReady
}) => {
  // Launches Adyen script/styles to render credit card inputs
  const { loaded } = useAdyenScript();
  const adyenInputs = useRef(null);
  const { language } = useLocale();
  const currentAdyenLocale = AdyenLocales[language];

  // Creates an instance of Adyen Checkout and renders credit card inputs when Adyen script is loaded
  useEffect(() => {
    if (loaded && window.AdyenCheckout) {
      const configuration = {
        locale: currentAdyenLocale,
        environment: process.env.REACT_APP_RBI_ADYEN_ENVIRONMENT,
        clientKey: process.env.REACT_APP_RBI_ADYEN_CLIENT_KEY
      };
      const checkout = new window.AdyenCheckout(configuration);
      checkout
        .create('card', {
          // accepted card types
          brands: [
            AdyenAcceptedCards.AMEX,
            AdyenAcceptedCards.MASTERCARD,
            AdyenAcceptedCards.VISA,
            AdyenAcceptedCards.DISCOVER
          ],
          styles: {
            base: {
              color: Styles.color.black,
              fontSize: '0.9375rem',
              fontFamily: Styles.fontFamily.body,
              fontWeight: Styles.fontWeight.light,
              padding: '0 .5rem'
            }
          },
          /** TO DO: Match placeholders/labels to existing fields
           * Optional. Define custom placeholders for the Card fields
           * https://docs.adyen.com/developers/checkout/api-integration/configure-secured-fields/styling-secured-fields
           */
          placeholders: {},
          /**
           * https://docs.adyen.com/payment-methods/cards/custom-card-integration#show-the-available-cards-in-your-payment-form
           * Using `onConfigSuccess` over `onLoad` because with "onLoad" the cards are not ready to use yet.
           */
          onConfigSuccess: onAdyenInputsReady,
          onChange,
          onError
        })
        .mount(adyenInputs.current);
    }
  }, [loaded, currentAdyenLocale, onChange, onError, onAdyenInputsReady]);

  return <AdyenInputsWrapper ref={adyenInputs} data-testid="adyen-credit-card-fields" />;
};

export default AdyenCustomCardInputs;
